/**
 * PasswordField Component
 *
 * A specialized form field for entering and managing passwords.
 *
 * ### Default Behavior:
 * - **Read-Only Mode**: By default, the password field displays in a read-only state. If a value exists,
 *   it is masked visually as asterisks (`'********'`), and a "Change" button is displayed next to the label.
 * - **Edit Mode**: Clicking the "Change" button clears the input value and makes the field writable.
 *   While editing, it behaves as a secure password input field (dots/stars masking) with an accompanying
 *   "Show" / "Hide" toggle button to temporarily reveal the plaintext password.
 * - **Post-Submission**: Once the form successfully submits, the field transitions back to the read-only state,
 *   masking the password back to asterisks.
 *
 * ### Supported Attribute Flags (via metadata.attributes or props):
 * 1. **`displayType`** (string, attribute only):
 *    - Controls the presentation of the read-only display.
 *    - If set to `'text'`, the read-only value will render as standard plaintext and the "Show" / "Hide" toggle
 *      button is omitted. When edited (via the "Change" button), it automatically falls back to a password
 *      field masked with the "Show" / "Hide" toggle.
 *    - If omitted or set to any other value, the read-only display defaults to a masked password field.
 *
 * 2. **`isCreate`** (boolean / string "true", via props or attribute):
 *    - Specifically for creation forms (e.g. creating a new user).
 *    - When `true`, the field starts automatically in edit mode, and the "Change" and "Cancel" buttons are hidden.
 *    - After a successful save transition, the field automatically transitions out of create mode into the regular
 *      read-only/masked state (showing `'********'` and the "Change" button).
 */
import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { unstable } from '@broadleaf/admin-components/';
import { get } from 'lodash';

const { FieldDecorations } = unstable;

const PasswordField = props => {
  const { metadata, formik, disabled, readOnly } = props;
  const { name, label } = metadata;
  const value = get(formik.values, name, '');
  const error = get(formik.errors, name);
  const touched = get(formik.touched, name, false);

  const [showPassword, setShowPassword] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);

  const isTextField = get(metadata, 'attributes.displayType') === 'text';

  const isCreateAttr =
    props.isCreate ||
    get(metadata, 'attributes.isCreate') === true ||
    get(metadata, 'attributes.isCreate') === 'true';

  const [isCreateMode, setIsCreateMode] = useState(isCreateAttr);

  // Sync isCreateMode if isCreateAttr changes from false to true
  useEffect(() => {
    if (isCreateAttr) {
      setIsCreateMode(true);
    }
  }, [isCreateAttr]);

  const isEditing = isCreateMode || (!readOnly && editingPassword);
  const showToggle = isEditing || !isTextField;

  const isSubmitting = formik.isSubmitting;
  const prevSubmitting = useRef(false);

  useEffect(() => {
    // Detect the transition from 'submitting' to 'not submitting' (Save Complete)
    if (prevSubmitting.current && !isSubmitting && !formik.isValidating) {
      // Manually reset this specific field to match the server's masked value
      const entered = get(formik.values, name, '');
      formik.setFieldValue(name, entered ? '********' : '', false);
      formik.setFieldTouched(name, false, false);
      setEditingPassword(false);
      setIsCreateMode(false);
    }
    prevSubmitting.current = isSubmitting;
  }, [isSubmitting, formik.isValidating, name]);

  const handleChangePasswordClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setEditingPassword(true);
    formik.setFieldValue(name, '', false);
  };

  const handleCancelChangeClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setEditingPassword(false);
    formik.setFieldValue(name, get(formik.initialValues, name, ''), false);
    formik.setFieldTouched(name, false, false);
  };

  const customMetadata = {
    ...metadata,
    label: (
      <span className="d-flex align-items-center">
        <span>{label}</span>
        {!isCreateMode && !readOnly && !disabled && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ml-2 tw-font-semibold tw-text-link-600 hover:tw-text-link-700"
            onClick={
              editingPassword
                ? handleCancelChangeClick
                : handleChangePasswordClick
            }
          >
            {editingPassword ? 'Cancel' : 'Change'}
          </button>
        )}
      </span>
    )
  };

  return (
    <FieldDecorations
      fullWidth
      {...props}
      metadata={customMetadata}
      formik={formik}
    >
      <div className="form-group">
        <div className="input-group">
          <input
            id={name}
            name={name}
            type={showToggle ? (showPassword ? 'text' : 'password') : 'text'}
            className={classNames('form-control', {
              'is-invalid': touched && !!error
            })}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={disabled}
            readOnly={!isCreateMode && (readOnly || !editingPassword)}
            placeholder={
              !isCreateMode && (readOnly || !editingPassword) ? 'none' : ''
            }
            value={value}
          />
          {showToggle && (
            <div className="input-group-append">
              <button
                className="btn btn-outline-secondary d-flex align-items-center"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={disabled || readOnly}
              >
                <i
                  className={classNames(
                    'fa mr-2',
                    showPassword ? 'fa-eye-slash' : 'fa-eye'
                  )}
                />
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          )}
        </div>
      </div>
    </FieldDecorations>
  );
};

export default PasswordField;
